import { mockCountryCodes } from '@panel/core/src/mockData';

/**
 * La función recibe un array de objetos con los datos del usuario y analiza sus elementos para devolver la información necesaria
 * @param {Array} userPhones
 * @param {boolean} hadPhoneType
 * @returns Se retorna un {} con los datos para los inputs del formulario
 */
// eslint-disable-next-line
export function dataContactAdapter(userPhones, hadPhoneType) {
  const siteId = process.env.REACT_APP_BUILD_SITE;
  const countryCodesAsArray = Object.values(mockCountryCodes);
  const defaultCountry = countryCodesAsArray.filter(item => {
    return item.sites.includes(siteId);
  });

  /**
   * La función evalúa el tipo de ordenamiento a realizar según la variable hadFirstType
   * @param {Array} userPhonesData
   * @param {boolean} hadFirstPhoneType indica que anteriormente el primer input tenía un dato de tipo 'PHONE', por lo tanto no se ordenará alfabéticamente para que el 'PHONE' se posicione en el primer input:
   * @returns devuelve un array de objetos
   */
  // eslint-disable-next-line consistent-return
  const getSortData = (userPhonesData, hadFirstPhoneType) => {
    // Caso donde (antes del update anterior) el primer input tenía el type === 'PHONE':
    //  Por lo tanto el ordenamiento será de la siguiente manera: [{type:'PHONE', ...},{type:'CELL_PHONE', ...},{type:'WHATSAPP', ...}]
    if (hadFirstPhoneType) {
      const inputsOrdered = [];
      userPhonesData.forEach(item => {
        if (item.type === 'PHONE') return inputsOrdered.splice(0, 0, item);
        if (item.type === 'CELL_PHONE') return inputsOrdered.splice(1, 0, item);
        if (item.type === 'WHATSAPP') return inputsOrdered.splice(2, 0, item);
        return null;
      });
      return inputsOrdered;
    }
    // Caso donde (antes del update anterior) el primer input tenía el type !== 'PHONE':
    //  Por lo tanto el ordenamiento será de la siguiente manera: [{type:'CELL_PHONE', ...},{type:'PHONE', ...},{type:'WHATSAPP', ...}]
    userPhonesData.sort((a, b) => {
      if (a.type > b.type) return 1;
      if (a.type < b.type) return -1;
      return 0;
    });
    return userPhonesData;
  };
  const dataOrdered = getSortData(userPhones, hadPhoneType);

  /**
   *  La función evalúa isValid y originalError para saber que teléfono debe devolver.
   * @param {object} contactPhone
   * @returns {string} Devuelve el número de teléfono o whatsapp
   */
  const getPhoneNumber = contactPhone => {
    // Formato inválido con error proveniente de la base (originalError = true) cuando se hace el getContactValidation():
    if (!contactPhone.isValid && contactPhone.originalError) return contactPhone.original;
    // Formato inválido con error proveniente del updateContactValidation() (originalError = false):
    if (!contactPhone.isValid && !contactPhone.originalError) return contactPhone.number;
    // Formato válido:
    return contactPhone.number;
  };

  const initialState = {
    firstPhone: {
      number: '',
      prefix: defaultCountry[0].value,
      type: 'CELL_PHONE',
      isValid: true
    },
    secondPhone: {
      number: '',
      prefix: defaultCountry[0].value,
      type: 'PHONE',
      isValid: true
    },
    whatsappPhone: {
      number: '',
      prefix: defaultCountry[0].value,
      type: 'WHATSAPP',
      isValid: true
    }
  };

  // CASO 1: El usuario no tiene ningún teléfono guardado:
  if (['', null].includes(userPhones) || (userPhones && userPhones.length === 0)) return initialState;

  // CASO 2: El usuario tiene 2 teléfonos y whatsapp (PHONE, CELL_PHONE y WHATSAPP):
  if (dataOrdered && dataOrdered.length === 3 && dataOrdered.every(phone => phone.number)) {
    return {
      firstPhone: {
        number: getPhoneNumber(dataOrdered[0]),
        prefix: dataOrdered[0].prefix || defaultCountry[0].value,
        type: dataOrdered[0].type,
        isValid: dataOrdered[0].isValid
      },
      secondPhone: {
        number: getPhoneNumber(dataOrdered[1]),
        prefix: dataOrdered[1].prefix || defaultCountry[0].value,
        type: dataOrdered[1].type,
        isValid: dataOrdered[1].isValid
      },
      whatsappPhone: {
        number: getPhoneNumber(dataOrdered[2]),
        prefix: dataOrdered[2].prefix || defaultCountry[0].value,
        type: dataOrdered[2].type,
        isValid: dataOrdered[2].isValid
      }
    };
  }

  // CASO 3: El usuario tiene 1 teléfono y whatsapp (PHONE ó CELL_PHONE y WHATSAPP):
  if (
    dataOrdered &&
    dataOrdered.length === 2 &&
    dataOrdered.every(phone => phone.number) &&
    dataOrdered.find(phone => phone.type === 'WHATSAPP')
  ) {
    return {
      firstPhone: {
        number: getPhoneNumber(dataOrdered[0]),
        prefix: dataOrdered[0].prefix || defaultCountry[0].value,
        type: dataOrdered[0].type,
        isValid: dataOrdered[0].isValid
      },
      secondPhone: {
        number: '',
        prefix: defaultCountry[0].value,
        type: dataOrdered[0].type === 'PHONE' ? 'CELL_PHONE' : 'PHONE',
        isValid: true
      },
      whatsappPhone: {
        number: getPhoneNumber(dataOrdered[1]),
        prefix: dataOrdered[1].prefix || defaultCountry[0].value,
        type: dataOrdered[1].type,
        isValid: dataOrdered[1].isValid
      }
    };
  }

  // CASO 4: El usuario sólo tiene 2 teléfonos (PHONE y CELL_PHONE):
  if (dataOrdered && dataOrdered.length === 2 && !dataOrdered.find(phone => phone.type === 'WHATSAPP')) {
    return {
      firstPhone: {
        number: getPhoneNumber(dataOrdered[0]),
        prefix: dataOrdered[0].prefix || defaultCountry[0].value,
        type: dataOrdered[0].type,
        isValid: dataOrdered[0].isValid
      },
      secondPhone: {
        number: getPhoneNumber(dataOrdered[1]),
        prefix: dataOrdered[1].prefix || defaultCountry[0].value,
        type: dataOrdered[1].type,
        isValid: dataOrdered[1].isValid
      },
      whatsappPhone: initialState.whatsappPhone
    };
  }

  // CASO 5: El usuario sólo tiene 1 teléfono (PHONE ó CELL_PHONE):
  if (dataOrdered && dataOrdered.length === 1 && !dataOrdered.find(phone => phone.type === 'WHATSAPP')) {
    return {
      firstPhone: {
        number: getPhoneNumber(dataOrdered[0]),
        prefix: dataOrdered[0].prefix || defaultCountry[0].value,
        type: dataOrdered[0].type,
        isValid: dataOrdered[0].isValid
      },
      secondPhone: {
        number: '',
        prefix: defaultCountry[0].value,
        type: dataOrdered[0].type === 'PHONE' ? 'CELL_PHONE' : 'PHONE',
        isValid: true
      },
      whatsappPhone: initialState.whatsappPhone
    };
  }

  // CASO 6: El usuario sólo tiene whatsapp (WHATSAPP):
  if (dataOrdered && dataOrdered.length === 1 && dataOrdered[0].type === 'WHATSAPP') {
    return {
      firstPhone: initialState.firstPhone,
      secondPhone: initialState.secondPhone,
      whatsappPhone: {
        number: getPhoneNumber(dataOrdered[0]),
        prefix: dataOrdered[0].prefix || defaultCountry[0].value,
        type: dataOrdered[0].type,
        isValid: dataOrdered[0].isValid
      }
    };
  }
}
